import React, { useState, useCallback, useEffect } from "react";
import { Box, Typography, Tabs, Tab } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ListIcon from "@mui/icons-material/List";
import { SavedFormsView } from "./SavedFormsView";
import { DeliveryForm } from "./DeliveryForm";
import { useParams, useNavigate } from "react-router";
import { useNotifyStatus } from "contexts/status";
import {
  DeliveryFormSave,
  useSaveDeliveryFormMutation,
  useGetDeliveryFormQuery,
  useListDeliveryFormsQuery,
  useUploadDeliveryFormPhotoMutation,
  useGetUploadedPhotoQuery,
} from "src/store/api/generatedApi";
import { useTenant } from "hooks/settings";

export const DeliveryFormPage: React.FC = () => {
  const tenantName = useTenant();
  const navigate = useNavigate();
  const notifyStatus = useNotifyStatus();
  const { formId } = useParams<{ formId?: string }>();

  // Tab state
  const [currentTab, setCurrentTab] = useState(0);

  // Form state
  const [formData, setFormData] = useState<DeliveryFormSave>({
    name: "",
    bay: "",
    commodity: "",
    mass_lbs: 0,
    deviations: "",
    notes: "",
    truck_number: "",
  });

  // Photo state (separate from form data since it's handled via upload endpoint)
  const [capturedPhoto, setCapturedPhoto] = useState<File | null>(null);

  // RTK Query hooks
  const [saveDeliveryForm] = useSaveDeliveryFormMutation();
  const [uploadPhoto] = useUploadDeliveryFormPhotoMutation();

  // Get current form if formId is set
  const {
    data: currentForm,
    isLoading: isLoadingForm,
    error: formError,
    isError: isFormError,
  } = useGetDeliveryFormQuery(
    {
      tenantName,
      formId: formId ? parseInt(formId) : -1,
    },
    {
      skip: !formId || formId === "" || isNaN(parseInt(formId)),
    }
  );

  // Get all forms for the saved forms view
  const { data: savedForms = [], isLoading: isLoadingForms } =
    useListDeliveryFormsQuery({
      tenantName,
    });

  // Get uploaded photo for current form
  const {
    data: uploadedPhoto,
    isLoading: isLoadingPhoto,
    refetch: refetchUploadedPhoto,
  } = useGetUploadedPhotoQuery(
    {
      tenantName,
      formId: formId ? parseInt(formId) : -1,
    },
    {
      skip: !formId || formId === "" || isNaN(parseInt(formId)),
    }
  );

  // Load form data when currentForm changes
  useEffect(() => {
    if (currentForm) {
      setFormData({
        id: currentForm.id,
        name: currentForm.name || "",
        bay: currentForm.bay || "",
        commodity: currentForm.commodity || "",
        mass_lbs: currentForm.mass_lbs || 0,
        deviations: currentForm.deviations || "",
        notes: currentForm.notes || "",
        truck_number: currentForm.truck_number || "",
      });
    }
  }, [currentForm]);

  // Clear photo when switching forms
  useEffect(() => {
    setCapturedPhoto(null);
  }, [formId]); // Clear photo whenever formId changes (including when it becomes undefined)

  // Auto-save on every change
  const handleFieldUpdate = useCallback(
    async (field: keyof DeliveryFormSave, value: string | number) => {
      const newFormData = { ...formData, [field]: value };
      setFormData(newFormData);

      try {
        const result = await saveDeliveryForm({
          tenantName,
          deliveryFormSave: newFormData,
        }).unwrap();

        // Navigate to the saved form if this was a new form
        if (!formId && result.id) {
          navigate(`/${tenantName}/admin/delivery-form/${result.id}`);
        }
      } catch (error) {
        console.error("Error saving form:", error);
      }
    },
    [formData, saveDeliveryForm, tenantName, formId, navigate]
  );

  // Handle form save (photos are now handled automatically in handlePhotoCapture)
  const handleSave = useCallback(
    async (data: DeliveryFormSave) => {
      try {
        await saveDeliveryForm({
          tenantName,
          deliveryFormSave: data,
        }).unwrap();

        // Navigate to the saved form if this was a new form
        // if (!formId && result.id) {
        //     navigate(`/${tenantName}/admin/delivery-form/${result.id}`);
        // }

        // Note: Photo upload is now handled automatically in handlePhotoCapture
      } catch (error) {
        console.error("Error saving delivery form:", error);
      }
    },
    [saveDeliveryForm, tenantName, formId, navigate]
  );

  const handleCompleteDelivery = useCallback(
    async (data: DeliveryFormSave) => {
      try {
        await saveDeliveryForm({
          tenantName,
          deliveryFormSave: data,
        }).unwrap();

        // Navigate to the saved form if this was a new form
        navigate(`/${tenantName}/admin/delivery-form`);

        // Note: Photo upload is now handled automatically in handlePhotoCapture
      } catch (error) {
        console.error("Error saving delivery form:", error);
      }
    },
    [saveDeliveryForm, tenantName, formId, navigate]
  );

  // Handle photo capture - auto-save form first, then upload photo
  const handlePhotoCapture = useCallback(
    async (file: File) => {
      setCapturedPhoto(file);

      try {
        // First ensure the form is saved to get an ID
        const formToSave = { ...formData };

        // If no notes, add a note to force save
        if (!formToSave.notes || formToSave.notes.trim() === "") {
          formToSave.notes = `Photo captured at ${new Date().toLocaleString()}`;
        }

        const result = await saveDeliveryForm({
          tenantName,
          deliveryFormSave: formToSave,
        }).unwrap();

        // Update local form data
        setFormData(result);

        // Navigate to the saved form if this was a new form
        if (!formId && result.id) {
          navigate(`/${tenantName}/admin/delivery-form/${result.id}`);
        }

        // Upload photo immediately with the form ID
        if (result.id) {
          await uploadPhoto({
            tenantName,
            formId: result.id,
            file: {
              name: file.name,
              content: await fileToBase64(file),
            },
          }).unwrap();

          // Refetch the uploaded photo to get the latest data
          await refetchUploadedPhoto();

          // Clear captured photo since it's now uploaded and we have the uploaded photo
          setCapturedPhoto(null);
        }
      } catch (error) {
        console.error("Error saving form and uploading photo:", error);
        notifyStatus({
          type: "error",
          text: "Failed to upload photo. Please try again.",
          seconds: 5,
        });
      }
    },
    [
      formData,
      saveDeliveryForm,
      uploadPhoto,
      tenantName,
      formId,
      navigate,
      notifyStatus,
      refetchUploadedPhoto,
    ]
  );

  // Handle tab change
  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setCurrentTab(newValue);
    },
    []
  );

  // Handle creating new form
  const handleNewForm = useCallback(() => {
    // Clear photo state first
    setCapturedPhoto(null);

    navigate(`/${tenantName}/admin/delivery-form`);
    setFormData({
      name: "",
      bay: "",
      commodity: "",
      mass_lbs: 0,
      deviations: "",
      notes: "",
      truck_number: "",
    });
    setCurrentTab(0);
  }, [navigate, tenantName]);

  // Handle form loading errors
  useEffect(() => {
    if (formId && !isLoadingForm) {
      if (isFormError) {
        // API error occurred
        console.error("Error loading form:", formError);
        const errorMessage =
          formError && "data" in formError
            ? (formError.data as any)?.detail || "Unknown error"
            : "Unknown error";
        notifyStatus({
          type: "error",
          text: `Error loading form: ${errorMessage}`,
          seconds: 5,
        });
        handleNewForm(); // Clear form and start fresh
      } else if (currentForm === null) {
        // API succeeded but form doesn't exist
        notifyStatus({
          type: "error",
          text: `Form with ID ${formId} not found. Starting a new form.`,
          seconds: 5,
        });
        handleNewForm(); // Clear form and start fresh
      }
    }
  }, [
    formId,
    isLoadingForm,
    isFormError,
    formError,
    currentForm,
    handleNewForm,
    notifyStatus,
  ]);

  // Handle editing existing form
  const handleEditForm = useCallback(
    (id: string) => {
      // Clear photo state when switching to different form
      setCapturedPhoto(null);

      navigate(`/${tenantName}/admin/delivery-form/${id}`);
      setCurrentTab(0);
    },
    [navigate, tenantName]
  );

  // Handle deleting form
  const handleDeleteForm = useCallback(async (id: string) => {
    // Note: We need a delete endpoint in the API
    console.log("Delete form:", id);
    // For now, just refresh the list
  }, []);

  // Utility function to convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data:image/...;base64, prefix
        const base64 = result.split(",")[1];
        if (base64) {
          resolve(base64);
        } else {
          reject(new Error("Failed to convert file to base64"));
        }
      };
      reader.onerror = reject;
    });
  };

  return (
    <Box
      sx={{
        p: 3,
        width: "95%",
        maxWidth: "1400px",
        mx: "auto",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <Typography variant="h3" gutterBottom align="center" sx={{ mb: 4 }}>
        Steel Mill Delivery Tracking
      </Typography>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="delivery form tabs"
        >
          <Tab
            icon={<AddIcon />}
            label={`New Delivery${
              currentForm ? ` (${currentForm.bay || "Untitled"})` : ""
            }`}
            iconPosition="start"
          />
          <Tab
            icon={<ListIcon />}
            label={`Saved Forms (${savedForms.length})`}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {currentTab === 0 ? (
        // New/Edit Delivery Form
        <DeliveryForm
          formData={formData}
          onSave={handleSave}
          onCompleteDelivery={handleCompleteDelivery}
          onFieldUpdate={handleFieldUpdate}
          capturedPhoto={capturedPhoto}
          onPhotoCapture={handlePhotoCapture}
          uploadedPhoto={uploadedPhoto}
          isLoadingPhoto={isLoadingPhoto}
        />
      ) : (
        // Saved Forms View
        <SavedFormsView
          savedDeliveries={savedForms}
          loading={isLoadingForms}
          error={null}
          onEditDelivery={handleEditForm}
          onDeleteDelivery={handleDeleteForm}
          onNewDelivery={handleNewForm}
        />
      )}
    </Box>
  );
};

export default DeliveryFormPage;
